import { desanitizeKey, formatTimestamp } from '@/helper/utils';
import { useOutsideClick } from '@/hooks/useOutsideClick';
import { useRef } from 'react';

interface Props {
  details?: any;

  isEdit?: boolean;
  onClose: () => void;
  onAccept: () => void;
}

export default function ResendNotification({ details, onClose, onAccept }: Props) {
  const modalRef = useRef<HTMLDivElement | null>(null);

  useOutsideClick(modalRef, onClose);

  return (
    <div className="fixed inset-0 bg-opacity-50 flex items-center justify-center z-50">
      <div
        className="absolute left-[255px] top-[72px] right-0 bottom-0 pointer-events-auto flex items-center justify-center"
        style={{
          background:
            'linear-gradient(174.07deg, rgba(173, 12, 98, 0.85) 0.38%, rgba(98, 28, 67, 0.85) 95.87%)',
        }}
      >
        <div className="bg-[#621C43] rounded-lg shadow-lg p-6 w-[600px]" ref={modalRef}>
          {/* Header */}
          <div className="flex justify-between items-center mb-3">
            <div className="flex items-center gap-3">
              <div className="bg-[#FFFFFF4D] p-2 rounded-full flex items-center justify-center">
                <svg
                  className="h-5 w-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-[#FFF8ED]">Resend Failed Notification</h3>
            </div>

            <button onClick={onClose} className="p-2 rounded-full hover:bg-[#FFFFFF33] transition">
              <svg
                className="h-5 w-5 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="border-t border-[#FFF8ED]/30 my-4"></div>

          {/* Content */}
          <p className="text-[#E2E2E2E5] text-lg mb-6 text-center">
            We're resending this notification to ensure all users receive it
          </p>

          {details.fileUrl && (
            <div className="mb-4 flex justify-center">
              <a
                href={details.fileUrl}
                download
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#4A082D] hover:bg-[#621C43] text-[#FFF8ED] rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                Download File
              </a>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-[#FFFFFF1A] p-4 rounded-lg border border-[#FFF8ED]/20">
              <h4 className="font-semibold text-[#FFF8ED] mb-3 flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-1l-4 4z"
                  />
                </svg>
                Notification Details
              </h4>
              <div className="space-y-2 text-sm">
                <DetailRow label="Type" value={desanitizeKey(details.notificationType)} />
                <DetailRow label="Title" value={details.title} />
                <DetailRow label="Message" value={details.message} />
              </div>
            </div>

            <div className="bg-[#FFFFFF1A] p-4 rounded-lg border border-[#FFF8ED]/20">
              <h4 className="font-semibold text-[#FFF8ED] mb-3 flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                Delivery Details
              </h4>
              <div className="space-y-2 text-sm">
                <DetailRow label="Delivery Type" value={desanitizeKey(details.deliveryType)} />
                <DetailRow label="Scheduled Time" value={formatTimestamp(details.scheduledTimer)} />
                <DetailRow label="Last Updated" value={formatTimestamp(details.updatedAt)} />
              </div>
            </div>
          </div>

          {/* Warning */}
          <div className="bg-[#FFF39452]/20 border border-[#FFF8ED]/40 text-[#FFF8ED] p-3 rounded-lg mb-5 flex items-start gap-2">
            <svg
              className="h-5 w-5 mt-0.5 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
            <p className="text-sm">
              This will immediately resend the notification to all targeted users. This action
              cannot be undone.
            </p>
          </div>

          {/* Buttons */}
          <div className="flex gap-4">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 text-sm rounded-md text-[#1A0206] bg-[#B0B0AF] hover:bg-[#d0cfcf]"
            >
              Cancel
            </button>
            <button
              onClick={onAccept}
              className="flex-1 px-4 py-2 text-sm rounded-md bg-[#FF383C] hover:bg-[#e22e32] text-[#FFF8ED] flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="m3 3 3 9-3 9 19-9Z"
                />
              </svg>
              Resend Notification
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="flex justify-between items-start">
      <span className="text-[#E2E2E2E5] text-xs">{label}:</span>
      <span className="text-[#FFF8ED] text-xs font-medium text-right max-w-[60%] break-words">
        {value ?? 'N/A'}
      </span>
    </div>
  );
}
